/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService, IWrapHTTPConfig } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { DistributorForm } from '../models/distributor-form';
import { DistributorHistoryForm } from '../models/distributor-history-form';
import { DistributorsSearchForm } from '../models/distributors-search-form';
import { EmptyResponse } from '../models/empty-response';
import { ResponseDistributorItemDto } from '../models/response-distributor-item-dto';
import { ResponsePageableListDtoDistributorHistoryDto } from '../models/response-pageable-list-dto-distributor-history-dto';
import { ResponsePageableListDtoDistributorListDto } from '../models/response-pageable-list-dto-distributor-list-dto';

@Injectable({
  providedIn: 'root',
})
export class AdminDistributorsApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getDistributor
   */
  static readonly GetDistributorPath = '/api/v1/admin/distributors/{distributorId}';

  /**
   * Get distributor
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDistributor()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getDistributor$Response(params: {
    distributorId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseDistributorItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.GetDistributorPath, 'get');
    if (params) {
      rb.path('distributorId', params.distributorId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseDistributorItemDto>;
      })
    );
  }

  /**
   * Get distributor
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getDistributor$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDistributor(params: {
    distributorId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseDistributorItemDto> {

    return this.wrapHTTPQuery(this.getDistributor$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseDistributorItemDto>) => r.body as ResponseDistributorItemDto)
    ), config);
  }

  /**
   * Path part for operation updateDistributor
   */
  static readonly UpdateDistributorPath = '/api/v1/admin/distributors/{distributorId}';

  /**
   * Update distributor
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDistributor()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private updateDistributor$Response(params: {
    distributorId: number;
    body: DistributorForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseDistributorItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.UpdateDistributorPath, 'put');
    if (params) {
      rb.path('distributorId', params.distributorId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseDistributorItemDto>;
      })
    );
  }

  /**
   * Update distributor
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateDistributor$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDistributor(params: {
    distributorId: number;
    body: DistributorForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseDistributorItemDto> {

    return this.wrapHTTPQuery(this.updateDistributor$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseDistributorItemDto>) => r.body as ResponseDistributorItemDto)
    ), config);
  }

  /**
   * Path part for operation deleteDistributor
   */
  static readonly DeleteDistributorPath = '/api/v1/admin/distributors/{distributorId}';

  /**
   * Delete distributor
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDistributor()` instead.
   *
   * This method doesn't expect any request body.
   */
  private deleteDistributor$Response(params: {
    distributorId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.DeleteDistributorPath, 'delete');
    if (params) {
      rb.path('distributorId', params.distributorId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Delete distributor
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteDistributor$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDistributor(params: {
    distributorId: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.deleteDistributor$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation searchDistributors
   */
  static readonly SearchDistributorsPath = '/api/v1/admin/distributors/search';

  /**
   * Search distributors
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchDistributors()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchDistributors$Response(params: {
    body: DistributorsSearchForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponsePageableListDtoDistributorListDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.SearchDistributorsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponsePageableListDtoDistributorListDto>;
      })
    );
  }

  /**
   * Search distributors
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchDistributors$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchDistributors(params: {
    body: DistributorsSearchForm
  }, config?: IWrapHTTPConfig
): Observable<ResponsePageableListDtoDistributorListDto> {

    return this.wrapHTTPQuery(this.searchDistributors$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponsePageableListDtoDistributorListDto>) => r.body as ResponsePageableListDtoDistributorListDto)
    ), config);
  }

  /**
   * Path part for operation searchDistributorHistories
   */
  static readonly SearchDistributorHistoriesPath = '/api/v1/admin/distributors/histories/{distributorId}';

  /**
   * Search distributor histories
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchDistributorHistories()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchDistributorHistories$Response(params: {
    distributorId: number;
    body: DistributorHistoryForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponsePageableListDtoDistributorHistoryDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.SearchDistributorHistoriesPath, 'post');
    if (params) {
      rb.path('distributorId', params.distributorId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponsePageableListDtoDistributorHistoryDto>;
      })
    );
  }

  /**
   * Search distributor histories
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchDistributorHistories$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchDistributorHistories(params: {
    distributorId: number;
    body: DistributorHistoryForm
  }, config?: IWrapHTTPConfig
): Observable<ResponsePageableListDtoDistributorHistoryDto> {

    return this.wrapHTTPQuery(this.searchDistributorHistories$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponsePageableListDtoDistributorHistoryDto>) => r.body as ResponsePageableListDtoDistributorHistoryDto)
    ), config);
  }

  /**
   * Path part for operation createDistributor
   */
  static readonly CreateDistributorPath = '/api/v1/admin/distributors';

  /**
   * Create distributor
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDistributor()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createDistributor$Response(params: {
    body: DistributorForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseDistributorItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminDistributorsApiService.CreateDistributorPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseDistributorItemDto>;
      })
    );
  }

  /**
   * Create distributor
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createDistributor$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDistributor(params: {
    body: DistributorForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseDistributorItemDto> {

    return this.wrapHTTPQuery(this.createDistributor$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseDistributorItemDto>) => r.body as ResponseDistributorItemDto)
    ), config);
  }

}
